import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import styles from "./BannerInformationDropdown.module.css";
import classNames from "classnames";
import { useDebounce } from "react-use";

import RedirectIcon from "../RedirectIcon";
import ChevronButton from "../ChevronButton";
import TextInput from "../TextInput";
import Button from "../Button";
import ButtonStyledRadioButtonGroup from "../ButtonStyledRadioButtonGroup";
import SelectPhotoModal from "../SelectPhotoModal";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAlignLeft,
  faAlignCenter,
  faAlignRight,
} from "@fortawesome/free-solid-svg-icons";

/**
 * Interactive component.
 * Dropdown that allows the user to modify a Banner feed item's information
 */
export function BannerInformationDropdown({
  bannerInformation,
  setBannerInformation,
  isOpen,
  onClick,
}) {
  const resolvedImg = bannerInformation.img ?? bannerInformation.image ?? "";

  const [isModal, setIsModal] = useState(false);
  const [tempPhoto, setTempPhoto] = useState(resolvedImg);

  // For TextInput component only.
  const [tempLink, setTempLink] = useState(bannerInformation.link ?? "");
  const [tempSrc, setTempSrc] = useState(resolvedImg);

  // Update link and src only after delay.
  // Debounce updates to parent `bannerInformation` to avoid excessive re-renders
  useDebounce(
    () => {
      if (tempLink !== (bannerInformation.link ?? "")) {
        setBannerInformation({ ...bannerInformation, link: tempLink });
      }
    },
    300,
    [tempLink]
  );

  useDebounce(
    () => {
      if (tempSrc !== resolvedImg) {
        setBannerInformation({ ...bannerInformation, img: tempSrc });
      }
    },
    300,
    [tempSrc]
  );

  useEffect(() => {
    const img = bannerInformation.img ?? bannerInformation.image ?? "";
    setTempPhoto(img);
    setTempSrc(img);
  }, [bannerInformation]);

  return (
    <>
      <section className={styles.container}>
        <div
          className={classNames(styles.content, {
            [styles["dropdown-container"]]: isOpen,
          })}
          onClick={() => {
            if (!isOpen) {
              onClick();
            }
          }}
        >
          <div
            className={classNames({
              [styles.hidden]: isOpen,
            })}
          >
            <p className={styles.title}>Banner Information:</p>
            <div className={styles["link-container"]}>
              <RedirectIcon />
              <p>{tempLink}</p>
            </div>
            <p className={styles.src}>{tempSrc}</p>
          </div>
          <div
            className={classNames({
              [styles.hidden]: !isOpen,
            })}
          >
            <p className={styles.title}>Banner Information:</p>
            <TextInput
              label="Link (/relative or http://...)"
              value={tempLink}
              setValue={(e) => setTempLink(e.target.value)}
            />
            <div className={styles["input-button-group"]}>
              <TextInput
                label="Image"
                value={tempSrc}
                setValue={(e) => setTempSrc(e.target.value)}
              />
              <Button text="Add Image" onClick={() => setIsModal(true)} />
            </div>
            <div className={styles["radio-group"]}>
              <ButtonStyledRadioButtonGroup
                currentlySelected={bannerInformation.align ?? bannerInformation.position ?? "center"}
                setCurrentlySelected={(e) => {
                  if ((bannerInformation.align ?? bannerInformation.position) !== e.target.value)
                    setBannerInformation({
                      ...bannerInformation,
                      align: e.target.value,
                    });
                }}
                options={[
                  {
                    value: "left",
                    buttonContent: <FontAwesomeIcon icon={faAlignLeft} />,
                  },
                  {
                    value: "center",
                    buttonContent: <FontAwesomeIcon icon={faAlignCenter} />,
                  },
                  {
                    value: "right",
                    buttonContent: <FontAwesomeIcon icon={faAlignRight} />,
                  },
                ]}
              />
            </div>
          </div>
        </div>
        <div className={styles.chevron}>
          <ChevronButton
            onClick={onClick}
            orientation={isOpen ? "down" : "right"}
          />
        </div>
        <SelectPhotoModal
          currentPhotoSrc={tempPhoto}
          setCurrentPhotoSrc={setTempPhoto}
          isVisible={isModal}
          setIsVisible={() => setIsModal(false)}
          onSubmit={(value) => {
            setIsModal(false);
            setBannerInformation({
              ...bannerInformation,
              img: value,
            });
          }}
          minWidth={null}
          minHeight={null}
        />
      </section>
    </>
  );
}

BannerInformationDropdown.propTypes = {
  /**
   * Object that contains the Banner feed item’s information.
   */
  bannerInformation: PropTypes.shape({
    link: PropTypes.string,
    img: PropTypes.string,
    align: PropTypes.oneOf(["left", "center", "right"]),
  }),
  /**
   * Function that allows the bannerInformation object to be reassigned.
   */
  setBannerInformation: PropTypes.func,
  /**
   * Boolean that determines if the dropdown is expanded.
   */
  isOpen: PropTypes.bool,
  /**
   * Function that fires when the dropdown is clicked.
   */
  onClick: PropTypes.func,
};

BannerInformationDropdown.defaultProps = {
  bannerInformation: {
    img: "https://picsum.photos/2000/100/?gravity=north",
    align: "left",
  },
};
